import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function MovingToVancouverFullGuide() {
  // 1) Unique reference ID (the slug in your ArticlesContext)
  const ref_id = "moving-to-vancouver-guide-start-here";

  // 2) Optional usage of your ArticlesContext
  const { articles } = useArticlesContext() || {};
  const ref_data = articles?.find((art) => art.slug === ref_id);

  // =====================================
  // 3) SECTION TITLES (Labels Map)
  // =====================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "1. Choose the Right Neighborhood & Housing",
    "section-2": "2. Budget for Vancouver’s High Cost of Living",
    "section-3": "3. Secure Employment or Income",
    "section-4": "4. Transportation & Getting Around",
    "section-5": "5. Brace for the Weather",
    "section-6": "6. Key Locations to Know",
    "section-7": "7. Embrace the Local Culture & Lifestyle",
    "section-8": "8. Understand Local Regulations & Best Practices",
    "section-9": "9. Build Your Community & Settle In",
    faq: "FAQ",
    conclusion: "Conclusion",
    disclaimers: "Disclaimers",
    references: "References",
  };

  // =====================================
  // 4) References Data
  // =====================================
  const referencesData = [
    {
      url: "https://vancouver.ca",
      label: "City of Vancouver – Official Website",
    },
    {
      url: "https://www.welcomebc.ca",
      label: "WelcomeBC (BC Government)",
    },
    {
      text: "Immigrant Services Society of BC (ISSofBC)",
    },
  ];

  // =====================================
  // 5) Locations for the Leaflet map
  // =====================================
  const locations = [
    {
      id: "city-hall",
      lat: 49.26083,
      lng: -123.11389,
      popupText: "Vancouver City Hall",
    },
    {
      id: "stanley-park",
      lat: 49.30426,
      lng: -123.14379,
      popupText: "Stanley Park & Seawall",
    },
    {
      id: "granville-island",
      lat: 49.27083,
      lng: -123.13417,
      popupText: "Granville Island Public Market",
    },
    {
      id: "vpl-central",
      lat: 49.27979,
      lng: -123.11567,
      popupText: "Vancouver Public Library – Central Branch",
    },
    {
      id: "yvr-airport",
      lat: 49.19472,
      lng: -123.18389,
      popupText: "Vancouver International Airport (YVR)",
    },
    {
      id: "ubc",
      lat: 49.26060,
      lng: -123.24599,
      popupText: "University of British Columbia (UBC)",
    },
    {
      id: "service-bc",
      lat: 49.28320,
      lng: -123.11535,
      popupText: "Service BC Centre (Downtown)",
    },
    {
      id: "pacific-central",
      lat: 49.27339,
      lng: -123.09914,
      popupText: "Pacific Central Station",
    },
    {
      id: "canada-place",
      lat: 49.28878,
      lng: -123.11134,
      popupText: "Canada Place & Vancouver Convention Centre",
    },
    {
      id: "ikea-richmond",
      lat: 49.19135,
      lng: -123.09951,
      popupText: "IKEA Richmond",
    },
  ];

  // Spot references for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>The Ultimate Vancouver Moving Guide: 10 Things You Must Know</title>
        <meta
          name="description"
          content="Moving to Vancouver? This extensive guide covers housing, budgeting, jobs, transportation, local culture, regulations, and key locations to ensure a smooth move."
        />
        <meta
          name="keywords"
          content="Vancouver, relocation, cost of living, housing, job hunting, public transport, newcomers"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/moving-to-vancouver-guide-start-here.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="The Ultimate Vancouver Moving Guide: 10 Things You Must Know"
        />
        <meta
          property="og:description"
          content="A comprehensive, step-by-step guide for anyone planning to move to Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/moving-to-vancouver-guide-start-here.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/moving-to-vancouver-guide-start-here.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="The Ultimate Vancouver Moving Guide: 10 Things You Must Know"
        />
        <meta
          name="twitter:description"
          content="Your all-in-one resource for moving to Vancouver—housing, costs, neighborhoods, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/moving-to-vancouver-guide-start-here.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TOC + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ARTICLE CONTENT */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A scenic view of Vancouver with mountains and the city skyline"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* Introduction */}
            <h2
              id="intro"
              ref={(el) => setSpotRef("intro", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              Introduction
            </h2>
            <p>
              Vancouver frequently tops lists of the world’s most livable cities,
              thanks to its breathtaking scenery, moderate climate, and inclusive
              culture. But high housing costs and the infamous “Raincouver”
              weather can surprise newcomers. This guide provides detailed,
              real-world tips to help you settle in—whether you’re a student,
              professional, or family. Read on for everything from finding a place
              to live and job-hunting to understanding local bylaws and embracing
              the city’s outdoor lifestyle.
            </p>

            {/* SECTION 1: Choose the Right Neighborhood & Housing */}
            <h2
              id="section-1"
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              1. Choose the Right Neighborhood &amp; Housing
            </h2>
            <p>
              Finding a suitable place to live is priority #1 when moving to
              Vancouver. Housing is notoriously expensive. A one-bedroom apartment
              in the City of Vancouver can easily cost upwards of $2,400 per
              month. Downtown (Yaletown, Coal Harbour) is among the priciest
              areas, while neighborhoods like East Vancouver, Burnaby, or
              New Westminster might be more budget-friendly with only a modestly
              longer commute.
            </p>
            <p>
              <strong>Students:</strong> If you’re attending UBC or SFU, on-campus
              housing is highly competitive—apply early. Alternatively, target
              areas with good transit links like Commercial Drive (East Van) or
              Metrotown (Burnaby).
            </p>
            <p>
              <strong>Families:</strong> Look into school catchment areas. Many
              families choose Dunbar, Kerrisdale, or North Vancouver for
              kid-friendly neighborhoods with parks and solid public schools.
            </p>
            <p>
              <strong>Professionals:</strong> If your workplace is downtown,
              living near a SkyTrain route can significantly cut commute times.
              Consider East Vancouver, Burnaby, or Mount Pleasant. Housing can
              also be found in shared rentals if you’re looking to split costs.
            </p>
            <p>
              Avoid scams by never sending money before viewing a rental. Get a
              standard BC tenancy agreement in writing, and be prepared to pay a
              damage deposit (often half a month’s rent). Some buildings are
              stricter about pets, so confirm policies if you’re bringing a furry
              friend.
            </p>

            {/* SECTION 2: Budget for Vancouver’s High Cost of Living */}
            <h2
              id="section-2"
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              2. Budget for Vancouver’s High Cost of Living
            </h2>
            <p>
              Vancouver is one of Canada’s most expensive cities. Beyond rent,
              groceries can be pricey, utilities add up, and dining out with
              tax/tip can shock newcomers. A typical monthly budget for a single
              person might be at least $3,000 if you factor in rent, groceries,
              phone/internet, and transit.
            </p>
            <p>
              <strong>Groceries:</strong> Shop around. Major chains like Safeway
              and Save-On-Foods are convenient but may have higher prices,
              especially for produce. Explore local produce markets or Chinatown
              for cheaper fruits and vegetables. Farmers’ markets are excellent
              for fresh options, though not always cheaper.
            </p>
            <p>
              <strong>Utilities &amp; Internet:</strong> If not included in rent,
              budget about $50–$100/month for BC Hydro. Internet/cable can run
              $80+/month. Cell phone plans in Canada are notoriously expensive, so
              research deals.
            </p>
            <p>
              <strong>Transportation:</strong> A monthly transit pass (Compass
              Card) starts around $107 for 1 zone. Driving costs (gas, insurance,
              parking) can quickly rise above $300/month. Many Vancouverites forgo
              car ownership to save money and avoid traffic or parking headaches.
            </p>
            <p>
              <strong>Save on Furniture:</strong> If you need to furnish a place,
              consider secondhand marketplaces or budget-friendly shops. For
              example, IKEA Richmond can help you set up a basic one-bedroom for
              around $1,000–$1,500.
            </p>

            {/* SECTION 3: Secure Employment or Income */}
            <h2
              id="section-3"
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              3. Secure Employment or Income
            </h2>
            <p>
              Unless you already have a job transfer, it’s wise to begin your job
              search well before arriving. Vancouver’s economy is strong in tech,
              film and TV, tourism, and finance, but competition and cost of
              living are high.
            </p>
            <p>
              <strong>Major Industries:</strong> Tech (Amazon, Microsoft, many
              startups), film (Hollywood North), tourism/hospitality, and finance
              are big. If you’re a newcomer, check your visa or work permit
              requirements on official government sites.
            </p>
            <p>
              <strong>Job Hunting Tips:</strong> Use job boards (LinkedIn, Indeed,
              Glassdoor). Networking is crucial: attend local meetups or
              professional events. Adapt your résumé to Canadian standards—concise
              and no personal photos. Prepare reference letters from past
              employers.
            </p>
            <p>
              <strong>Local Wages:</strong> BC’s minimum wage is above $17/hour,
              but Vancouver’s cost of living often requires a higher salary to
              live comfortably. A typical comfortable single-person salary might
              be $70k+ annually.
            </p>

            {/* SECTION 4: Transportation & Getting Around */}
            <h2
              id="section-4"
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              4. Transportation &amp; Getting Around
            </h2>
            <p>
              One of Vancouver’s perks is excellent public transit (TransLink).
              The SkyTrain, buses, and SeaBus are efficient, and many locals live
              car-free. Grab a Compass Card to pay for transit—fares allow free
              transfers within 90 minutes. A monthly pass is $107+ (1 zone).
            </p>
            <p>
              Cycling is also a popular option with extensive bike lanes; helmets
              are mandatory, and lights are required at night. If you do drive,
              switch your out-of-province license to a BC license within 90 days,
              and insure your car with ICBC. Parking can be expensive downtown, so
              factor that into your budget.
            </p>
            <p>
              Car-sharing services (Evo, Modo) and ride-hailing (Uber, Lyft) are
              readily available for those occasional trips where transit isn’t
              enough.
            </p>

            {/* SECTION 5: Brace for the Weather */}
            <h2
              id="section-5"
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              5. Brace for the Weather
            </h2>
            <p>
              Vancouver’s climate is mild by Canadian standards but often very
              rainy in fall/winter. The average winter temperature hovers around
              0–5°C, with occasional snowfalls. Invest in waterproof gear—jacket,
              boots, umbrella—and don’t let rain deter you from enjoying outdoor
              life. Summers (June–September) are typically warm, sunny, and
              glorious, with highs around 23°C. 
            </p>
            <p>
              <strong>Daylight Variation:</strong> Winter days can get dark by
              4:30pm, while summer enjoys long evenings until nearly 10pm. Some
              newcomers find winters gloomy, so consider a daylight lamp if you’re
              prone to Seasonal Affective Disorder (SAD).
            </p>
            <p>
              <strong>Outdoor Lifestyle:</strong> Vancouverites don’t hide from
              rain—they hike, cycle, or run year-round. During summer, beaches and
              parks fill up. Embrace both sides of the climate to get the most out
              of living here.
            </p>

            {/* SECTION 6: Key Locations to Know */}
            <h2
              id="section-6"
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              6. Key Locations to Know
            </h2>
            <p>
              Understanding Vancouver’s layout and visiting some iconic places
              will help you settle in quickly. Here are 10 important or popular
              spots:
            </p>
            <ul>
              <li
                id="city-hall"
                ref={(el) => setSpotRef("city-hall", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Vancouver City Hall</strong>{" "}
                <Marker
                  coordY={49.26083}
                  coordX={-123.11389}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Seat of municipal government at 453 W 12th Ave. Apply for
                parking permits, pay property taxes, and attend local council
                sessions.
              </li>
              <li
                id="stanley-park"
                ref={(el) => setSpotRef("stanley-park", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Stanley Park &amp; Seawall</strong>{" "}
                <Marker
                  coordY={49.30426}
                  coordX={-123.14379}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A 1,001-acre urban park offering beaches, the Vancouver
                Aquarium, and a 10km seawall loop for walking, jogging, or biking.
              </li>
              <li
                id="granville-island"
                ref={(el) => setSpotRef("granville-island", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Granville Island Public Market</strong>{" "}
                <Marker
                  coordY={49.27083}
                  coordX={-123.13417}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A foodie haven with fresh produce, artisan crafts, and
                performance spaces. Take a tiny ferry across False Creek for extra
                fun.
              </li>
              <li
                id="vpl-central"
                ref={(el) => setSpotRef("vpl-central", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Vancouver Public Library – Central Branch</strong>{" "}
                <Marker
                  coordY={49.27979}
                  coordX={-123.11567}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Iconic “Colosseum-style” building offering free workshops,
                internet, and extensive reading materials.
              </li>
              <li
                id="yvr-airport"
                ref={(el) => setSpotRef("yvr-airport", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Vancouver International Airport (YVR)</strong>{" "}
                <Marker
                  coordY={49.19472}
                  coordX={-123.18389}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – In Richmond, ~25 minutes from downtown by Canada Line SkyTrain.
                A hub for flights across the globe.
              </li>
              <li
                id="ubc"
                ref={(el) => setSpotRef("ubc", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>University of British Columbia (UBC)</strong>{" "}
                <Marker
                  coordY={49.26060}
                  coordX={-123.24599}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A sprawling campus at the western tip of the city. Home to the
                Museum of Anthropology and the UBC Botanical Garden.
              </li>
              <li
                id="service-bc"
                ref={(el) => setSpotRef("service-bc", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Service BC Centre (Downtown)</strong>{" "}
                <Marker
                  coordY={49.28320}
                  coordX={-123.11535}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – At 888 Dunsmuir St. A place to handle various provincial
                services, from ID cards to MSP details.
              </li>
              <li
                id="pacific-central"
                ref={(el) => setSpotRef("pacific-central", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Pacific Central Station</strong>{" "}
                <Marker
                  coordY={49.27339}
                  coordX={-123.09914}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – The main hub for intercity buses and VIA Rail trains, at 1150
                Station St.
              </li>
              <li
                id="canada-place"
                ref={(el) => setSpotRef("canada-place", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Canada Place &amp; Vancouver Convention Centre</strong>{" "}
                <Marker
                  coordY={49.28878}
                  coordX={-123.11134}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Iconic sails along the waterfront. Cruise ship terminal, FlyOver
                Canada, and year-round events.
              </li>
              <li
                id="ikea-richmond"
                ref={(el) => setSpotRef("ikea-richmond", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>IKEA Richmond</strong>{" "}
                <Marker
                  coordY={49.19135}
                  coordX={-123.09951}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Popular stop for affordable home furniture at 3320 Jacombs Rd in
                Richmond. Ideal for newcomers needing quick furnishings.
              </li>
            </ul>

            {/* SECTION 7: Embrace the Local Culture & Lifestyle */}
            <h2
              id="section-7"
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              7. Embrace the Local Culture &amp; Lifestyle
            </h2>
            <p>
              Vancouver is highly multicultural. You’ll hear many languages—from
              Cantonese and Punjabi to Tagalog and Farsi. This diversity influences
              food, festivals, and daily life. Despite its laid-back West Coast
              vibe, it can be challenging to form friendships (the “Vancouver
              freeze”). Overcome this by joining community groups, Meetups, or
              volunteering.
            </p>
            <p>
              Locals are environmentally conscious: recycling, composting, and
              carrying reusable bags is the norm. You’ll see yoga studios on every
              corner, and many people run or cycle year-round. Vancouver has a
              lively arts scene, though smaller than Toronto or Montreal. Don’t
              miss the Vancouver Art Gallery, local theaters, and various
              festivals (film, jazz, folk). 
            </p>
            <p>
              Dining out? You’ll find global cuisines everywhere. Must-try: sushi,
              local seafood (salmon, oysters), plus an ever-growing craft beer
              scene. Remember that many restaurants close relatively early, and
              last call downtown is often 2am or earlier.
            </p>

            {/* SECTION 8: Understand Local Regulations & Best Practices */}
            <h2
              id="section-8"
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              8. Understand Local Regulations &amp; Best Practices
            </h2>
            <p>
              Adhering to city bylaws is important. Vancouver strictly enforces
              waste sorting (recycling, organics, garbage). Noise bylaws limit
              loud disturbances, especially late at night. Smoking (including
              cannabis) is banned in most public areas and near building entrances.
            </p>
            <p>
              If you’re renting, familiarize yourself with BC’s Residential
              Tenancy Act. Landlords must follow rules for rent increases and
              evictions. If you own a car, you’ll need a residential parking
              permit in many neighborhoods. And for moving day, you might need a
              temporary no-parking permit if using a large truck.
            </p>
            <p>
              Enroll in the BC Medical Services Plan (MSP) quickly to access
              public health care. Note there can be a 3-month wait, so get private
              insurance to cover you in the interim.
            </p>

            {/* SECTION 9: Build Your Community & Settle In */}
            <h2
              id="section-9"
              ref={(el) => setSpotRef("section-9", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              9. Build Your Community &amp; Settle In
            </h2>
            <p>
              Settling into a new city isn’t just about logistics. It’s also about
              feeling at home. Get involved—join local clubs, attend events, or
              volunteer. Vancouver has many communities, from sports leagues to
              cultural associations. 
            </p>
            <p>
              If you’re dealing with homesickness or the “Vancouver freeze,”
              balance connecting with fellow newcomers from your home country and
              making local friends. Explore beyond downtown—day trips to Whistler,
              the Gulf Islands, or the Sunshine Coast reveal BC’s stunning
              landscapes and can help you appreciate the region.
            </p>
            <p>
              Give yourself at least a year to truly feel settled. The first
              winter can be tough if you’re used to sunny climates, but summer
              will reward you with spectacular beaches, hikes, and vibrant
              festivals.
            </p>

            {/* FAQ Section */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)} style={{ scrollMarginTop: "80px" }}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. How much money do I need to live comfortably in Vancouver?</h3>
              <p>
                A single person might spend around $3,000/month (including rent).
                Families could need $5,000–$6,000/month or more. The biggest
                factor is rent, which can exceed $2,400 for a 1-bedroom. Earning
                at least $70k/year (single) or $120k+ (household) is considered
                moderate for a comfortable life here.
              </p>

              <h3>2. Do I need a car, or can I rely on public transit?</h3>
              <p>
                Many Vancouverites live car-free. TransLink’s extensive system
                (SkyTrain, buses, SeaBus) plus a strong biking infrastructure can
                cover most needs. Owning a car adds insurance, fuel, and parking
                expenses. It’s optional unless you live far from transit or need
                a vehicle regularly.
              </p>

              <h3>3. Which neighborhood is best for me?</h3>
              <p>
                It depends on lifestyle and budget. Downtown is vibrant but
                expensive. Kitsilano is near beaches and popular with students
                and young professionals. East Vancouver offers somewhat more
                affordable rents and a bohemian vibe. Families often choose
                Dunbar, Kerrisdale, or North Vancouver for schools and parks.
              </p>

              <h3>4. What should I do first when I arrive?</h3>
              <p>
                Secure housing or temporary accommodation, get a SIN (if new to
                Canada), apply for MSP health coverage, open a bank account,
                swap your license to BC if needed, and explore your neighborhood
                for essentials (grocery, pharmacy, transit stops).
              </p>

              <h3>5. Is Vancouver safe? Are there areas I should avoid?</h3>
              <p>
                Overall, Vancouver is quite safe with a low violent crime rate.
                The Downtown Eastside has visible homelessness and drug use, so
                newcomers might feel uneasy, but it’s not typically dangerous if
                you use common sense. Like any city, secure your belongings, lock
                your bike, and stay aware at night.
              </p>
            </div>

            {/* Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Moving to Vancouver can be both exciting and challenging. With
              high living costs, rainy winters, and a competitive job market, it
              pays to prepare diligently. However, once settled, you’ll discover
              why people worldwide love calling this city home—natural beauty,
              cultural diversity, and a relaxed West Coast ethos await. Embrace
              the journey, and soon you’ll feel right at home in Raincouver.
            </p>

            {/* Disclaimers */}
            <h2
              id="disclaimers"
              style={{ scrollMarginTop: "80px", fontStyle: "italic" }}
            >
              Disclaimers
            </h2>
            <p>
              <strong>Information Accuracy:</strong> This guide aims to be as
              accurate as possible, but local policies and costs change. Always
              verify critical details (visa rules, MSP coverage, landlord-tenant
              laws) with official sources.
            </p>
            <p>
              <strong>Not Legal Advice:</strong> The content here is general
              guidance, not legal or financial counsel. Consult qualified
              professionals for personalized advice.
            </p>
            <p>
              <strong>Health &amp; Safety:</strong> Follow official resources for
              updates on public health or emergency procedures. Outdoor activities
              (hiking, skiing) come with risks—prepare responsibly.
            </p>
            <p>
              <strong>Personal Responsibility:</strong> Every newcomer’s situation
              differs. Use this guide as a starting point, and remember to budget
              a safety net for unexpected costs or delays.
            </p>
            <p>
              <strong>Endorsements &amp; References:</strong> Mention of any
              business, service, or location is for example only; it’s not an
              endorsement. Always do your own research.
            </p>

            {/* BOTTOM COMPONENT (References, etc.) */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}